import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.pics';

const faqs = [
  {
    q: 'What is Litbuy Spreadsheet?',
    a: 'Litbuy Spreadsheet is a specialized data management platform designed for product-centric businesses. It combines spreadsheet functionality with e-commerce tools for inventory management, bulk editing, category organization, and workflow automation.',
  },
  {
    q: 'Is Litbuy Spreadsheet free to use?',
    a: 'Litbuy Spreadsheet operates on a subscription-based pricing model. For the most current pricing information, including any free trial options, please visit the official Litbuy Spreadsheet website through our main website link.',
  },
  {
    q: 'How do beginners start using Litbuy Spreadsheet?',
    a: 'Beginners should start by creating an account, exploring the dashboard, and importing a small set of product data. We recommend reading our Beginner Guide and watching any official tutorials. The platform is designed to be intuitive, with most users becoming comfortable within a few hours.',
  },
  {
    q: 'Can Litbuy Spreadsheet handle large product catalogs?',
    a: 'Yes, Litbuy Spreadsheet is built to handle extensive product databases with thousands of entries. The bulk editing, advanced filtering, and optimized data processing ensure smooth performance even with large catalogs across multiple categories.',
  },
  {
    q: 'Is my data safe with Litbuy Spreadsheet?',
    a: 'Litbuy Spreadsheet employs AES-256 encryption for data at rest and TLS 1.3 for data in transit. The platform supports multi-factor authentication and role-based access controls. However, as with any cloud service, we recommend maintaining your own regular data backups.',
  },
  {
    q: 'Does Litbuy Spreadsheet integrate with e-commerce platforms?',
    a: 'Litbuy Spreadsheet offers integration capabilities with several major e-commerce platforms and marketplaces. The import and export functions support standard formats like CSV and Excel that are compatible with most selling platforms.',
  },
  {
    q: 'What are the best alternatives to Litbuy Spreadsheet?',
    a: 'The best alternative depends on your needs. Google Sheets is the most popular free option with strong collaboration features. Microsoft Excel offers more advanced formulas and offline capabilities. Airtable provides flexible database views. We recommend evaluating each based on your specific use case.',
  },
  {
    q: 'Can I use Litbuy Spreadsheet offline?',
    a: 'Litbuy Spreadsheet is primarily a cloud-based platform, meaning an internet connection is required for most features. For offline spreadsheet needs, desktop applications like Microsoft Excel may be more suitable.',
  },
  {
    q: 'How does Litbuy Spreadsheet compare to Google Sheets?',
    a: 'While Google Sheets is a versatile free tool, Litbuy Spreadsheet provides specialized e-commerce features such as built-in category management, bulk product editing, and product-focused templates that would require extensive manual setup in Google Sheets.',
  },
  {
    q: 'What types of businesses benefit most from Litbuy Spreadsheet?',
    a: 'E-commerce sellers, dropshipping businesses, inventory managers, product sourcing professionals, and anyone managing extensive product listings across multiple categories benefit most from Litbuy Spreadsheet specialized features.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.a,
    },
  })),
};

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <title>Litbuy Spreadsheet FAQ — Frequently Asked Questions | litbuyspreadsheets.pics</title>
      <meta name="description" content="Find answers to the most frequently asked questions about Litbuy Spreadsheet. Learn about features, pricing, security, integrations, and how Litbuy Spreadsheet compares to alternatives." />
      <link rel="canonical" href={`${siteUrl}/faq`} />
      <meta property="og:title" content="Litbuy Spreadsheet FAQ — Frequently Asked Questions | litbuyspreadsheets.pics" />
      <meta property="og:description" content="Find answers to the most frequently asked questions about Litbuy Spreadsheet. Features, pricing, security, integrations, and more." />
      <meta property="og:url" content={`${siteUrl}/faq`} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="Litbuy Spreadsheet FAQ — Frequently Asked Questions | litbuyspreadsheets.pics" />
      <meta name="twitter:description" content="Find answers to the most frequently asked questions about Litbuy Spreadsheet." />
      <JsonLd data={faqSchema} />
      <Navbar />
      <main className="pt-24 pb-16 px-6 md:px-10 lg:px-16">
        <article className="max-w-3xl mx-auto">
          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full border border-accent-200 text-xs font-medium text-accent-600 tracking-wide mb-4">
              FAQ
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              Litbuy Spreadsheet FAQ
            </h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              Find answers to the most frequently asked questions about Litbuy Spreadsheet. Cannot find what you are looking for? Visit our main website for more resources.
            </p>
          </header>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <details key={idx} className="group bg-background-100 rounded-xl p-4 md:p-5 border border-background-200/60 cursor-pointer">
                <summary className="flex items-center justify-between text-sm md:text-base font-semibold text-foreground-900 list-none">
                  {faq.q}
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-3 text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </summary>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>

          <div className="mt-10 bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-3">Still Have Questions?</h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-5">
              Visit our main website to explore the full Litbuy Spreadsheet platform and get direct support from the official team.
            </p>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Visit Main Website
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </a>
          </div>

          <div className="mt-6 flex items-center gap-4 text-sm">
            <a href="/" className="text-foreground-600 hover:text-primary-500 transition-colors duration-200 border-b border-transparent hover:border-primary-300 pb-0.5 whitespace-nowrap">
              &larr; Back to Home
            </a>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}